import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../lib/api';
import type { ComparisonResponse, ComparisonRow } from '../types/api';
import { tx } from '../i18n';

interface ComparisonState {
  comparison: ComparisonResponse | null;
  loading: boolean;
  error: string | null;
  /** Set when the last add was refused by the free limit, so the caller can offer the upgrade. */
  limitReached: boolean;
  refresh: () => void;
  add: (propertyAddressId: string, analysisId: string | null) => Promise<boolean>;
  remove: (propertyAddressId: string) => Promise<void>;
  clearLimitFlag: () => void;
  isShortlisted: (propertyAddressId: string) => boolean;
  /** The comparison row for an address in a search result, matched on postcode and address line. */
  rowFor: (postcode: string, addressLine: string) => ComparisonRow | null;
}

const ComparisonContext = createContext<ComparisonState | null>(null);

/**
 * Holds the comparison list for the whole app.
 *
 * <p>Shared state rather than per-page fetching, because the header counter and the compare page
 * have to agree: a tenant who has used all three free slots should see that everywhere at once.
 */
export function ComparisonProvider({ children }: { children: ReactNode }) {
  const [comparison, setComparison] = useState<ComparisonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    api
      .comparison(signal)
      .then((result) => {
        setComparison(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (signal?.aborted) {
          return;
        }
        setError(cause instanceof ApiError ? cause.message : tx('We could not load your comparison.', 'Karşılaştırmanız yüklenemedi.',
    'Nie udało się wczytać porównania.',
    'Não foi possível carregar a sua comparação.',
    'No se ha podido cargar tu comparación.'));
      })
      .finally(() => {
        if (!signal?.aborted) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const add = useCallback(async (propertyAddressId: string, analysisId: string | null) => {
    try {
      const updated = await api.addToComparison(propertyAddressId, analysisId);
      setComparison(updated);
      setLimitReached(false);
      setError(null);
      return true;
    } catch (cause) {
      if (cause instanceof ApiError && cause.isCompareLimit) {
        setLimitReached(true);
        return false;
      }
      setError(cause instanceof ApiError ? cause.message : tx('We could not add that home.', 'Bu konut eklenemedi.',
    'Nie udało się dodać tego mieszkania.',
    'Não foi possível adicionar esta casa.',
    'No se ha podido añadir esta vivienda.'));
      return false;
    }
  }, []);

  const remove = useCallback(async (propertyAddressId: string) => {
    try {
      const updated = await api.removeFromComparison(propertyAddressId);
      setComparison(updated);
      setLimitReached(false);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : tx('We could not update your comparison.', 'Karşılaştırmanız güncellenemedi.',
    'Nie udało się zaktualizować porównania.',
    'Não foi possível atualizar a sua comparação.',
    'No se ha podido actualizar tu comparación.'));
    }
  }, []);

  const value = useMemo<ComparisonState>(
    () => ({
      comparison,
      loading,
      error,
      limitReached,
      refresh: () => load(),
      add,
      remove,
      clearLimitFlag: () => setLimitReached(false),
      isShortlisted: (propertyAddressId: string) =>
        (comparison?.rows ?? []).some((row) => row.propertyAddressId === propertyAddressId),
      rowFor: (postcode: string, addressLine: string) => {
        const wantedPostcode = normalise(postcode);
        const wantedLine = normalise(addressLine);
        return (
          (comparison?.rows ?? []).find(
            (row) =>
              normalise(row.postcode) === wantedPostcode &&
              normalise(row.addressLine) === wantedLine,
          ) ?? null
        );
      },
    }),
    [comparison, loading, error, limitReached, load, add, remove],
  );

  return <ComparisonContext.Provider value={value}>{children}</ComparisonContext.Provider>;
}

function normalise(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Access to the shared comparison state. */
export function useComparison(): ComparisonState {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used inside a ComparisonProvider');
  }
  return context;
}
